import type { PagedResult } from "@/lib/pagination";

export interface OverviewCounts {
  users: number;
  usersPendingInvites: number;
  teams: number;
  teamsWithoutRules: number;
  gateways: number;
  gatewaysOffline: number;
  connectors: number;
  connectorsOffline: number;
  resources: number;
}

export interface AttentionResource {
  id: string;
  name: string;
  type: string;
  reason: string;
}

export interface Overview {
  counts: OverviewCounts;
  resources: AttentionResource[];
  resourcesNeedingAttention: number;
}

export interface AuditEvent {
  id: string;
  description: string;
  userName: string | null;
  userEmail: string | null;
  userAvatarId: string | null;
  createdAt: string;
}

export interface Recording {
  id: string;
  resourceName: string;
  requestedByUserName: string | null;
  requestedByUserEmail: string | null;
  requestedByUserAvatarId: string | null;
  requestedAt: string;
  durationSeconds: number;
  connectionMethod: string;
  resourceKind: string | null;
}

const RECORDING_TYPES: Record<string, { label: string; path: string }> = {
  SSH: { label: "SSH", path: "ssh-recordings" },
  RDP: { label: "RDP", path: "rdp-recordings" },
  KubernetesAPI: { label: "Kubernetes", path: "kubernetes-recordings" },
  PostgresWireProtocol: { label: "Postgres", path: "database-recordings" },
  MySQLProtocol: { label: "MySQL", path: "database-recordings" },
};

export function recordingType(connectionMethod: string) {
  return (
    RECORDING_TYPES[connectionMethod] ?? {
      label: connectionMethod,
      path: "ssh-recordings",
    }
  );
}

// MySQL and MariaDB share a connection method, so their kind names them.
export function recordingLabel(
  recording: Pick<Recording, "connectionMethod" | "resourceKind">,
) {
  if (recording.connectionMethod === "MySQLProtocol" && recording.resourceKind)
    return recording.resourceKind;

  return recordingType(recording.connectionMethod).label;
}

export interface AccessibleResource {
  id: string;
  name: string;
  type: string;
  kind: string | null;
}

const RESOURCE_PATHS: Record<string, string> = {
  VirtualMachine: "virtual-machines",
  Database: "databases",
  KubernetesCluster: "kubernetes-clusters",
};

export function resourcePath(type: string): string {
  return `/resources/${RESOURCE_PATHS[type] ?? "virtual-machines"}`;
}

const ATTENTION_DETAIL: Record<string, string> = {
  SessionsRefused: "Review the host identity and its required policy.",
  Unreachable:
    "Check that the gateways and connectors on its routes are online and that firewalls allow traffic to the resource.",
  NoRoute: "Assign a gateway to route connections to this resource.",
  NotChecked: "Wait for the next probe or check that the gateway is running.",
};

export function attentionDetail(reason: string): string {
  return ATTENTION_DETAIL[reason] ?? "This resource is currently unavailable.";
}

export function attentionSummary(count: number): string {
  return `${count} resource${count === 1 ? " needs" : "s need"} attention`;
}

export type Get = <T>(path: string) => Promise<T | null>;

export const overviewPath = (orgId: string) =>
  `/organizations/${orgId}/overview`;

export const eventsPath = (orgId: string) =>
  `/organizations/${orgId}/audit-logs?page=1&pageSize=10`;

export const recordingsPath = (orgId: string) =>
  `/organizations/${orgId}/recordings`;

export type EventsResult = PagedResult<AuditEvent>;
export type MyResourcesResult = PagedResult<AccessibleResource>;
